import { Link } from 'react-router-dom'
import { useSummary } from '@/lib/summary'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { SeverityBadge } from '@/components/SeverityBadge'
import { GpuClusterTile } from '@/components/GpuClusterTile'
import { HealthClusterTile } from '@/components/HealthClusterTile'
import { PowerCard } from '@/components/PowerCard'
import { PowerHistoryCard } from '@/components/PowerHistoryCard'
import { RackTempCard } from '@/components/RackTempCard'
import { FreeboxCard } from '@/components/FreeboxCard'
import { bytes, relative } from '@/lib/format'
import type { Severity } from '@/lib/alerts'

export function DashboardPage() {
  const { data, isLoading } = useSummary()

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground">Live state of your Docker Swarm.</p>
      </div>

      {isLoading || !data ? (
        <Skeleton className="h-32" />
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Link to="/nodes" className="block">
              <Card className="h-full hover:border-primary/50 transition cursor-pointer">
                <CardHeader><CardTitle className="text-base">Nodes</CardTitle></CardHeader>
                <CardContent>
                  <div className="text-2xl font-semibold">{data.cluster.nodes}</div>
                  <div className="text-xs text-muted-foreground">View</div>
                </CardContent>
              </Card>
            </Link>
            <Link to="/services" className="block">
              <Card className="h-full hover:border-primary/50 transition cursor-pointer">
                <CardHeader><CardTitle className="text-base">Services</CardTitle></CardHeader>
                <CardContent>
                  <div className="text-2xl font-semibold">{data.cluster.services}</div>
                  <div className="text-xs text-muted-foreground">{data.cluster.stacks} stacks</div>
                </CardContent>
              </Card>
            </Link>
            <Link to="/alerts" className="block">
              <Card className="h-full hover:border-primary/50 transition cursor-pointer">
                <CardHeader><CardTitle className="text-base">Open alerts</CardTitle></CardHeader>
                <CardContent>
                  <div className="text-2xl font-semibold">{data.alerts.open}</div>
                  <div className="text-xs text-muted-foreground">{data.alerts.critical} critical</div>
                </CardContent>
              </Card>
            </Link>
            <Link to="/errors" className="block">
              <Card className="h-full hover:border-primary/50 transition cursor-pointer">
                <CardHeader><CardTitle className="text-base">Findings (active)</CardTitle></CardHeader>
                <CardContent>
                  <div className="text-2xl font-semibold">{data.findings.active}</div>
                  <div className="text-xs text-muted-foreground">View</div>
                </CardContent>
              </Card>
            </Link>
            <HealthClusterTile />
            <GpuClusterTile />
          </div>

          <PowerCard />

          <PowerHistoryCard />

          <RackTempCard />

          <FreeboxCard />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Recent alerts</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {data.recent_alerts.length === 0 && (
                  <p className="text-sm text-muted-foreground">None.</p>
                )}
                {data.recent_alerts.map((a) => (
                  <div key={a.id} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <SeverityBadge severity={a.severity as Severity} />
                      <Link to={`/alerts/${a.id}`} className="truncate hover:underline">{a.title}</Link>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {a.closed_at ? `closed ${relative(a.closed_at)}` : `opened ${relative(a.opened_at)}`}
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex-row items-center justify-between">
                <CardTitle className="text-base">Recent backups</CardTitle>
                <Link
                  to="/backups"
                  className="text-xs text-muted-foreground hover:underline"
                >
                  View all
                </Link>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.recent_backups.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    No runs yet. <Link to="/backups" className="hover:underline">Configure</Link>
                  </p>
                )}
                {data.recent_backups.map((b) => {
                  const label = b.service_name ?? '—'
                  const duration =
                    b.duration_s != null ? `${b.duration_s.toFixed(1)}s` : null
                  const when = b.finished_at
                    ? relative(b.finished_at)
                    : b.started_at
                      ? 'running'
                      : 'queued'
                  return (
                    <div key={b.id} className="flex items-start gap-2 text-sm">
                      <span
                        className={`mt-1.5 inline-block h-2 w-2 rounded-full ${
                          b.status === 'ok'
                            ? 'bg-emerald-500'
                            : b.status === 'failed'
                              ? 'bg-destructive'
                              : 'bg-amber-500'
                        }`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium truncate">{label}</span>
                          {b.stack && (
                            <span className="text-xs text-muted-foreground">
                              {b.stack}
                            </span>
                          )}
                          {b.kind === 'manual' && (
                            <span className="text-[10px] text-muted-foreground border border-border rounded px-1">
                              manual
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-2">
                          <span>{b.status}</span>
                          {b.size_bytes != null && <span>· {bytes(b.size_bytes)}</span>}
                          {duration && <span>· {duration}</span>}
                          <span>· {when}</span>
                        </div>
                        {b.status === 'failed' && b.error_preview && (
                          <div
                            className="mt-1 text-[11px] text-destructive/80 line-clamp-2"
                            title={b.error_preview}
                          >
                            {b.error_preview}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  )
}
